import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import LeadComposeEmailModal from '../components/LeadComposeEmailModal'; // @@LEADDETAIL_COMPOSE_v8@@

const STATUSES = ['new', 'contacted', 'qualified', 'won', 'lost'];

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [emails, setEmails] = useState([]);
  // @@LEADDETAIL_NOTES_v33@@
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editMsg, setEditMsg] = useState('');

  async function load() {
    const res = await apiFetch(`/api/leads/${id}`);
    if (res.ok) { const d = await res.json(); setLead(d.lead); }
    else if (res.status === 404) { setMsg('Lead not found.'); }
    loadEmails();
    loadNotes();
  }
  useEffect(() => { load(); }, [id]);

  async function updateStatus(status) {
    const res = await apiFetch(`/api/leads/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (res.ok) { const d = await res.json(); setLead({ ...lead, ...d.lead }); }
  }

  async function remove() {
    if (!window.confirm('Delete this lead? This cannot be undone.')) return;
    const res = await apiFetch(`/api/leads/${id}`, { method: 'DELETE' });
    if (res.ok) navigate('/leads');
  }

  async function loadEmails() {
    const res = await apiFetch(`/api/leads/${id}/emails`);
    if (res.ok) { const d = await res.json(); setEmails(d.emails || []); }
  }

  async function loadNotes() {
    const res = await apiFetch(`/api/leads/${id}/notes`);
    if (res.ok) { const d = await res.json(); setNotes(d.notes || []); }
  }

  async function addNote() {
    const body = noteDraft.trim();
    if (!body) return;
    setNoteBusy(true);
    try {
      const res = await apiFetch(`/api/leads/${id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      if (res.ok) { setNoteDraft(''); loadNotes(); }
    } finally { setNoteBusy(false); }
  }

  function startEdit() {
    setEditMsg('');
    setEditForm({
      first_name: lead.first_name || '', last_name: lead.last_name || '',
      email: lead.email || '', phone: lead.phone || '',
      company: lead.company || '', source: lead.source || '',
      value: lead.value != null ? String(lead.value) : '', notes: lead.notes || '',
    });
    setEditing(true);
  }

  async function saveEdit() {
    setEditMsg('');
    const res = await apiFetch(`/api/leads/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editForm),
    });
    if (res.ok) { const d = await res.json(); setLead({ ...lead, ...d.lead }); setEditing(false); }
    else { const d = await res.json().catch(() => ({})); setEditMsg(d.error || 'Could not save.'); }
  }

  const editField = (k) => (
    <input className="hq-input" value={editForm[k]}
      onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} style={{ minWidth: 200 }} />
  );

  function copyPixel() {
    if (!lead) return;
    const tag = `<img src="${lead.pixel_url}" width="1" height="1" alt="" style="display:none" />`;
    navigator.clipboard.writeText(tag).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }

  function fmt(s) { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch { return '—'; } }

  if (msg) return <div className="hq-page-content"><div className="hq-page-head"><h1>Leads</h1><p>{msg}</p></div></div>;
  if (!lead) return <div className="hq-page-content"><div className="hq-page-head"><p>Loading…</p></div></div>;

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>{lead.company || 'No company'}</h1>
        <p>{`${lead.first_name} ${lead.last_name}`.trim() || 'Lead'} · {lead.email || 'no email'}</p>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Details</h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <select className="hq-select" value={lead.status} onChange={(e) => updateStatus(e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {editing ? (
              <>
                <button className="hq-btn" onClick={saveEdit}>Save</button>
                <button className="hq-signout" style={{ width: 'auto', padding: '0 14px', marginTop: 0 }} onClick={() => setEditing(false)}>Cancel</button>
              </>
            ) : (
              <button className="hq-btn" onClick={startEdit}>Edit</button>
            )}
          </div>
        </div>
        <table className="hq-table">
          <tbody>
            {editing && editForm ? (
              <>
                <tr><td className="hq-role">First name</td><td>{editField('first_name')}</td></tr>
                <tr><td className="hq-role">Last name</td><td>{editField('last_name')}</td></tr>
                <tr><td className="hq-role">Email</td><td>{editField('email')}</td></tr>
                <tr><td className="hq-role">Phone</td><td>{editField('phone')}</td></tr>
                <tr><td className="hq-role">Company</td><td>{editField('company')}</td></tr>
                <tr><td className="hq-role">Source</td><td>{editField('source')}</td></tr>
                <tr><td className="hq-role">Value</td><td>{editField('value')}</td></tr>
                <tr><td className="hq-role">Notes</td><td>
                  <textarea className="hq-input" value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    style={{ width: '100%', minWidth: 200, height: 70, padding: 10, resize: 'vertical' }} />
                </td></tr>
              </>
            ) : (
              <>
                <tr><td className="hq-role">Phone</td><td>{lead.phone || '—'}</td></tr>
                <tr><td className="hq-role">Source</td><td>{lead.source || '—'}</td></tr>
                <tr><td className="hq-role">Value</td><td>{lead.value != null ? lead.value : '—'}</td></tr>
                <tr><td className="hq-role">Notes</td><td>{lead.notes || '—'}</td></tr>
              </>
            )}
          </tbody>
        </table>
        {editMsg && <p className="hq-msg" style={{ padding: '0 20px 16px' }}>{editMsg}</p>}
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Email</h2>
          <button className="hq-btn" onClick={() => setComposeOpen(true)}>Compose email</button>
        </div>
        <table className="hq-table">
          <thead><tr><th>Subject</th><th>To</th><th>From</th><th>Sent</th><th>Status</th></tr></thead>
          <tbody>
            {emails.map((e) => (
              <tr key={e.id}>
                <td>{e.subject || '—'}</td>
                <td>{e.to_name || e.to_email}</td>
                <td style={{ color: 'var(--hq-muted)', fontSize: 12 }}>{e.from_email}</td>
                <td>{fmt(e.created_at)}</td>
                <td>
                  <span className={e.status === 'sent' ? 'hq-pill hq-on' : 'hq-pill hq-off'}>
                    {e.status === 'sent' ? 'Sent' : 'Failed'}
                  </span>
                </td>
              </tr>
            ))}
            {emails.length === 0 && (
              <tr><td className="hq-empty" colSpan={5}>No emails sent yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>Notes</h2></div>
        <div style={{ padding: '16px 20px 0' }}>
          <textarea className="hq-input" placeholder="Add a note…" value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            style={{ width: '100%', height: 70, padding: 10, resize: 'vertical' }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '10px 0 4px' }}>
            <button className="hq-btn" onClick={addNote} disabled={noteBusy || !noteDraft.trim()}>
              {noteBusy ? 'Adding…' : 'Add note'}
            </button>
          </div>
        </div>
        <table className="hq-table">
          <tbody>
            {notes.map((n) => (
              <tr key={n.id}>
                <td style={{ whiteSpace: 'pre-wrap' }}>{n.body}</td>
                <td style={{ color: 'var(--hq-muted)', fontSize: 12, whiteSpace: 'nowrap', textAlign: 'right', verticalAlign: 'top' }}>
                  {fmt(n.created_at)}{n.author ? ` · ${n.author}` : ''}
                </td>
              </tr>
            ))}
            {notes.length === 0 && (
              <tr><td className="hq-empty" colSpan={2}>No notes yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Open tracking</h2>
          <button className="hq-btn" onClick={copyPixel}>{copied ? 'Copied!' : 'Copy pixel'}</button>
        </div>
        <div style={{ padding: '16px 20px', color: 'var(--hq-muted)', fontSize: 13 }}>
          Paste the copied tag into an email to this lead. Opens are logged below.
          Opened <strong style={{ color: 'var(--hq-txt)' }}>{lead.open_count || 0}</strong> times.
        </div>
        <table className="hq-table">
          <thead><tr><th>Opened</th><th>Device</th></tr></thead>
          <tbody>
            {(lead.opens || []).map((o, idx) => (
              <tr key={idx}><td>{fmt(o.opened_at)}</td><td style={{ color: 'var(--hq-muted)', fontSize: 12 }}>{(o.user_agent || '—').slice(0, 60)}</td></tr>
            ))}
            {(!lead.opens || lead.opens.length === 0) && (
              <tr><td className="hq-empty" colSpan={2}>No opens recorded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button className="hq-signout" style={{ width: 'auto', padding: '0 16px' }} onClick={() => navigate('/leads')}>Back</button>
        <button className="hq-signout" style={{ width: 'auto', padding: '0 16px', borderColor: 'rgba(255,90,82,.3)', color: '#ff5a52' }} onClick={remove}>Delete lead</button>
      </div>

      {composeOpen && (
        <LeadComposeEmailModal
          lead={lead}
          onClose={() => setComposeOpen(false)}
          onSent={() => { loadEmails(); load(); }}
        />
      )}
    </div>
  );
}
